import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode
} from 'react';
import axios from 'axios';
import { config } from '@/config';
import { mapBackendUserToUser } from '@/feature/auth/utils/mapBackendUser';
import { getBackendPermissions } from '@/feature/auth/utils/permissions';
import { canUseCurrentDomainSession } from '@/feature/auth/utils/authRouting';
import {
  getCurrentTenantHost,
  getStoredTenantId,
  getTenantHostHeaders,
  getTenantRequestHeaders,
  resolveTenantByHost
} from '@/feature/auth/utils/tenantStorage';
import {
  PersonalClient,
  RefreshTokenRequest,
  TokenResponse,
  TokensClient
} from '@/rest/apiv2.hsdesk';
import { User } from '@/types';

interface UserContextType {
  accessToken: string | null;
  user: User | null;
  permissions: string[] | null;
  isLoading: boolean;
  setAccessToken: React.Dispatch<React.SetStateAction<string | null>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setPermissions: React.Dispatch<React.SetStateAction<string[] | null>>;
  logout: () => void;
  refreshPermissions: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasRefreshed = useRef(false);
  const permissionRequest = useRef(0);
  const currentAccessToken = useRef(accessToken);
  currentAccessToken.current = accessToken;
  const invalidatePermissionRequest = useCallback(() => {
    permissionRequest.current++;
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (!accessToken) return;
    const tenantId = getStoredTenantId();
    const requestId = ++permissionRequest.current;
    try {
      const next = await getBackendPermissions(
        axios.create({
          baseURL: config.apiUrl,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...getTenantRequestHeaders()
          }
        })
      );
      if (
        requestId !== permissionRequest.current ||
        currentAccessToken.current !== accessToken ||
        getStoredTenantId() !== tenantId
      )
        return;
      setPermissions((current) => {
        const normalized = [...new Set(next)].sort();
        return current &&
          [...current].sort().join('\n') === normalized.join('\n')
          ? current
          : normalized;
      });
    } catch {
      // Keep the last known menu during a temporary connection failure.
      // API authorization still enforces current access on every operation.
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') void refreshPermissions();
    };
    const interval = window.setInterval(refreshVisible, 60_000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
      invalidatePermissionRequest();
    };
  }, [accessToken, refreshPermissions, invalidatePermissionRequest]);

  useEffect(() => {
    if (hasRefreshed.current) return;
    hasRefreshed.current = true;

    const tryRefresh = async () => {
      try {
        try {
          await resolveTenantByHost(getCurrentTenantHost());
        } catch {
          // App-domain sessions and unknown hosts fall back to stored tenant.
        }

        const storedRefreshToken = sessionStorage.getItem('refreshToken');
        const storedAccessToken = sessionStorage.getItem('accessToken');

        if (!storedRefreshToken) {
          setIsLoading(false);
          return;
        }

        const client = new TokensClient(
          config.apiUrl,
          axios.create({ headers: getTenantHostHeaders() })
        );
        const request = new RefreshTokenRequest({
          token: storedAccessToken ?? undefined,
          refreshToken: storedRefreshToken
        });

        const tenantId = getStoredTenantId();
        const response: TokenResponse = await client.refresh(tenantId, request);

        if (!response.token || !response.refreshToken) {
          clearTokens();
          return;
        }

        setAccessToken(response.token);
        sessionStorage.setItem('accessToken', response.token);
        sessionStorage.setItem('refreshToken', response.refreshToken);

        const authorizedAxios = axios.create({
          baseURL: config.apiUrl,
          headers: {
            Authorization: `Bearer ${response.token}`,
            ...getTenantRequestHeaders()
          }
        });
        const personalClient = new PersonalClient(
          config.apiUrl,
          authorizedAxios
        );
        const profileResponse = await personalClient.getProfile();
        const permissionsResponse =
          await getBackendPermissions(authorizedAxios);

        if (!canUseCurrentDomainSession(permissionsResponse)) {
          clearTokens();
          return;
        }

        setPermissions(permissionsResponse);
        setUser(mapBackendUserToUser(profileResponse));
      } catch (err) {
        console.warn('Sesja wygasla lub brak aktywnej sesji');
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    tryRefresh();
  }, []);

  const clearTokens = () => {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    setAccessToken(null);
    setPermissions(null);
    setUser(null);
  };

  const logout = () => {
    clearTokens();
  };

  return (
    <UserContext.Provider
      value={{
        accessToken,
        setAccessToken,
        permissions,
        setPermissions,
        user,
        setUser,
        isLoading,
        refreshPermissions,
        logout
      }}
    >
      {!isLoading && children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser musi byc uzywany wewnatrz <UserProvider>');
  }

  return context;
};
